import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import Layout from "../components/Layout";
import { Bot, Plus, Pencil, Trash2, X, BookOpen, Send, RotateCcw, Save, Lock } from "lucide-react";
import { toast } from "sonner";

const MODEL_LABELS = {
  "claude-opus-5": "Claude Opus 5 — paling pintar (default)",
  "claude-sonnet-5": "Claude Sonnet 5 — seimbang, lebih hemat",
  "claude-haiku-4-5": "Claude Haiku 4.5 — tercepat & termurah",
};
const inputCls = "w-full px-3 py-2 border border-[#E4E4E7] text-sm outline-none focus:border-[#002FA7]";
const emptyKb = { title: "", content: "", active: true };

function ConfigCard() {
  const [conf, setConf] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/wa/ai/config").then((r) => setConf(r.data)).catch(() => toast.error("Gagal memuat pengaturan AI"));
  }, []);

  const save = async (override) => {
    setSaving(true);
    try {
      const { models, ...body } = override || conf;
      const { data } = await api.put("/wa/ai/config", body);
      setConf(data);
      toast.success(override ? (data.wa_ai_enabled ? "AI Agent AKTIF — mulai membalas pesan masuk" : "AI Agent dinonaktifkan") : "Pengaturan AI disimpan");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  if (!conf) return <div className="bg-white border border-[#E4E4E7] p-6 text-sm text-[#52525B]">Memuat…</div>;
  const set = (k, v) => setConf({ ...conf, [k]: v });
  const noKey = !conf.anthropic_api_key;

  return (
    <div className="bg-white border border-[#E4E4E7]" data-testid="ai-config-card">
      <div className="px-4 py-3 border-b border-[#E4E4E7] flex items-center justify-between gap-3">
        <div className="label-cap text-[10px]">Pengaturan AI Agent</div>
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <span className={`text-xs font-bold ${conf.wa_ai_enabled ? "text-[#16A34A]" : "text-[#A1A1AA]"}`}>{conf.wa_ai_enabled ? "AKTIF" : "NONAKTIF"}</span>
          <input type="checkbox" data-testid="ai-enabled-toggle" checked={!!conf.wa_ai_enabled} onChange={(e) => save({ ...conf, wa_ai_enabled: e.target.checked })} disabled={saving} className="w-4 h-4 accent-[#16A34A]" />
        </label>
      </div>
      <div className="p-4 space-y-4">
        {conf.wa_ai_enabled && noKey && (
          <div className="px-3 py-2 border border-[#D97706]/30 bg-[#D97706]/5 text-[#B45309] text-xs">Isi API key Anthropic dulu, AI belum bisa membalas tanpa key.</div>
        )}
        <div className="px-3 py-2 border border-[#DC2626]/20 bg-[#DC2626]/5 text-[#B91C1C] text-xs">
          Pastikan <b>AI agent di Fonnte sudah dimatikan</b> sebelum mengaktifkan ini, supaya pelanggan tidak menerima dua balasan.
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="label-cap block mb-1.5">API Key Anthropic</label>
            <input type="password" data-testid="ai-api-key" value={conf.anthropic_api_key} onChange={(e) => set("anthropic_api_key", e.target.value)} placeholder="sk-ant-… (kosongkan bila tidak diubah)" className={inputCls + " font-mono text-xs"} />
            <p className="text-[10px] text-[#52525B] mt-1">Buat di console.anthropic.com → API Keys. Tersimpan di server, tidak ditampilkan lagi.</p>
          </div>
          <div>
            <label className="label-cap block mb-1.5">Model</label>
            <select data-testid="ai-model" value={conf.wa_ai_model} onChange={(e) => set("wa_ai_model", e.target.value)} className={inputCls + " bg-white"}>
              {(conf.models || Object.keys(MODEL_LABELS)).map((m) => <option key={m} value={m}>{MODEL_LABELS[m] || m}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="label-cap block mb-1.5">Persona & instruksi tambahan</label>
          <textarea data-testid="ai-persona" rows={4} value={conf.wa_ai_persona} onChange={(e) => set("wa_ai_persona", e.target.value)} className={inputCls} />
          <p className="text-[10px] text-[#52525B] mt-1">Gaya bicara, sapaan, hal yang boleh/tidak boleh. Fakta (harga, layanan, area) taruh di Knowledge Base di bawah.</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <label className="label-cap block mb-1.5">Jeda saat admin balas (menit)</label>
            <input type="number" min={0} max={1440} value={conf.wa_ai_pause_minutes} onChange={(e) => set("wa_ai_pause_minutes", Number(e.target.value))} className={inputCls} />
          </div>
          <div>
            <label className="label-cap block mb-1.5">Tunggu sebelum balas (detik)</label>
            <input type="number" min={0} max={120} value={conf.wa_ai_debounce_sec} onChange={(e) => set("wa_ai_debounce_sec", Number(e.target.value))} className={inputCls} />
          </div>
          <div>
            <label className="label-cap block mb-1.5">Maks balasan AI / jam / chat</label>
            <input type="number" min={1} max={100} value={conf.wa_ai_max_per_hour} onChange={(e) => set("wa_ai_max_per_hour", Number(e.target.value))} className={inputCls} />
          </div>
          <label className="flex items-center gap-2 text-xs mt-5 cursor-pointer">
            <input type="checkbox" checked={!!conf.wa_ai_skip_technicians} onChange={(e) => set("wa_ai_skip_technicians", e.target.checked)} className="accent-[#16A34A]" />
            Jangan balas nomor karyawan
          </label>
        </div>
        <div className="border-t border-[#E4E4E7] pt-4">
          <div className="label-cap text-[10px] mb-3">Notifikasi WhatsApp ke Admin</div>
          <div className="grid sm:grid-cols-3 gap-4 items-start">
            <div>
              <label className="label-cap block mb-1.5">No WA admin penerima</label>
              <input data-testid="ai-notify-phone" value={conf.wa_ai_notify_phone || ""} onChange={(e) => set("wa_ai_notify_phone", e.target.value)} placeholder="08…" className={inputCls + " font-mono"} />
              <p className="text-[10px] text-[#52525B] mt-1">Kosongkan untuk mematikan semua notifikasi. Nomor ini tidak dibalas AI.</p>
            </div>
            <label className="flex items-center gap-2 text-xs mt-6 cursor-pointer">
              <input type="checkbox" data-testid="ai-notify-booking" checked={!!conf.wa_ai_notify_booking} onChange={(e) => set("wa_ai_notify_booking", e.target.checked)} className="accent-[#16A34A]" />
              Pelanggan setuju dijadwalkan
            </label>
            <label className="flex items-center gap-2 text-xs mt-6 cursor-pointer">
              <input type="checkbox" data-testid="ai-notify-handoff" checked={!!conf.wa_ai_notify_handoff} onChange={(e) => set("wa_ai_notify_handoff", e.target.checked)} className="accent-[#16A34A]" />
              Chat perlu diambil alih admin
            </label>
          </div>
        </div>
        <div className="flex justify-end">
          <button onClick={() => save()} disabled={saving} data-testid="ai-config-save" className="px-4 py-2.5 bg-[#16A34A] text-white text-xs font-bold uppercase flex items-center gap-1.5 hover:bg-[#15803D] disabled:opacity-50">
            <Save size={14} /> {saving ? "Menyimpan…" : "Simpan"}
          </button>
        </div>
      </div>
    </div>
  );
}

function KnowledgeBase() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(emptyKb);
  const [editingId, setEditingId] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/wa/kb");
      setList(data || []);
    } catch (_) {}
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(emptyKb); setEditingId(null); setModal(true); };
  const openEdit = (k) => { setForm({ title: k.title, content: k.content, active: k.active !== false }); setEditingId(k.id); setModal(true); };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) await api.put(`/wa/kb/${editingId}`, form);
      else await api.post("/wa/kb", form);
      toast.success("Knowledge base disimpan");
      setModal(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (k) => {
    try {
      await api.put(`/wa/kb/${k.id}`, { title: k.title, content: k.content, active: k.active === false });
      load();
    } catch (_) { toast.error("Gagal mengubah status"); }
  };

  const remove = async () => {
    try {
      await api.delete(`/wa/kb/${confirmDel.id}`);
      toast.success("Dihapus");
      setConfirmDel(null);
      load();
    } catch (_) { toast.error("Gagal menghapus"); }
  };

  return (
    <div className="bg-white border border-[#E4E4E7]" data-testid="kb-card">
      <div className="px-4 py-3 border-b border-[#E4E4E7] flex items-center justify-between gap-3">
        <div className="label-cap text-[10px]">Knowledge Base ({list.filter((k) => k.active !== false).length} aktif)</div>
        <button onClick={openCreate} data-testid="kb-add" className="px-3 py-2 bg-[#16A34A] text-white text-xs font-bold uppercase flex items-center gap-1.5 hover:bg-[#15803D]">
          <Plus size={13} /> Tambah
        </button>
      </div>
      {loading ? (
        <div className="p-6 text-center text-sm text-[#52525B]">Memuat…</div>
      ) : list.length === 0 ? (
        <div className="p-6 text-center text-sm text-[#52525B]" data-testid="kb-empty">
          Belum ada knowledge base. Tambahkan info seperti daftar harga, area layanan, jam operasional, cara pemesanan, garansi, dan FAQ.
        </div>
      ) : (
        <div className="divide-y divide-[#E4E4E7]">
          {list.map((k) => (
            <div key={k.id} className={`px-4 py-3 flex items-start gap-3 ${k.active === false ? "opacity-50" : ""}`} data-testid={`kb-row-${k.id}`}>
              <div className="mt-0.5 text-[#002FA7]"><BookOpen size={16} /></div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm">{k.title}{k.active === false && <span className="ml-2 text-[10px] font-bold text-[#A1A1AA]">NONAKTIF</span>}</div>
                <div className="text-xs text-[#52525B] whitespace-pre-wrap break-words mt-0.5 line-clamp-3">{k.content}</div>
              </div>
              <div className="flex gap-1 shrink-0 items-center">
                <input type="checkbox" title="Aktif" checked={k.active !== false} onChange={() => toggleActive(k)} className="accent-[#16A34A] mr-1" />
                <button onClick={() => openEdit(k)} className="p-2 hover:bg-[#F4F4F5] border border-[#E4E4E7]"><Pencil size={14} /></button>
                <button onClick={() => setConfirmDel(k)} className="p-2 hover:bg-[#DC2626]/10 text-[#DC2626] border border-[#E4E4E7]"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setModal(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white border border-[#E4E4E7] max-w-2xl w-full" data-testid="kb-form">
            <div className="px-6 py-4 border-b border-[#E4E4E7] flex items-center justify-between">
              <h2 className="font-heading text-xl font-bold">{editingId ? "Ubah" : "Tambah"} Knowledge Base</h2>
              <button type="button" onClick={() => setModal(false)} className="p-2 hover:bg-[#F4F4F5]"><X size={18} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="label-cap block mb-1.5">Judul / Topik<span className="text-[#DC2626]">*</span></label>
                <input data-testid="kb-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required placeholder="mis. Daftar Harga, Area Layanan, Garansi" className={inputCls} />
              </div>
              <div>
                <label className="label-cap block mb-1.5">Isi<span className="text-[#DC2626]">*</span></label>
                <textarea data-testid="kb-content" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} required rows={12}
                  placeholder={"Tulis informasi selengkap mungkin, mis.:\n- Survey & deteksi kebocoran: Rp …\n- Area layanan: …\n- Tanya-jawab: Q: … A: …"} className={inputCls} />
              </div>
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-[#16A34A]" /> Aktif (dipakai AI)
              </label>
            </div>
            <div className="px-6 py-4 border-t border-[#E4E4E7] flex justify-end gap-2">
              <button type="button" onClick={() => setModal(false)} className="px-4 py-2 text-xs font-bold uppercase border border-[#E4E4E7]">Batal</button>
              <button type="submit" disabled={saving} data-testid="kb-submit" className="px-4 py-2 text-xs font-bold uppercase bg-[#16A34A] text-white hover:bg-[#15803D] disabled:opacity-50">Simpan</button>
            </div>
          </form>
        </div>
      )}

      {confirmDel && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setConfirmDel(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white border border-[#E4E4E7] max-w-sm w-full p-6">
            <h3 className="font-heading text-lg font-bold mb-2">Hapus knowledge base?</h3>
            <p className="text-sm text-[#52525B] mb-6">"{confirmDel.title}" akan dihapus permanen.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmDel(null)} className="px-4 py-2 text-xs font-bold uppercase border border-[#E4E4E7]">Batal</button>
              <button onClick={remove} className="px-4 py-2 text-xs font-bold uppercase bg-[#DC2626] text-white">Hapus</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Tester() {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);

  const send = async () => {
    const t = text.trim();
    if (!t || busy) return;
    const next = [...msgs, { from_me: false, body: t }];
    setMsgs(next);
    setText("");
    setBusy(true);
    try {
      const { data } = await api.post("/wa/ai/test", { messages: next.map(({ from_me, body }) => ({ from_me, body })) });
      setMsgs((p) => [...p, { from_me: true, body: data.reply || "(AI memilih tidak membalas)", handoff: data.handoff, reason: data.handoff_reason, booking: data.booking_agreed, summary: data.booking_summary }]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menguji AI", { duration: 8000 });
    } finally {
      setBusy(false);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    }
  };

  return (
    <div className="bg-white border border-[#E4E4E7] flex flex-col" style={{ height: 520 }} data-testid="ai-tester">
      <div className="px-4 py-3 border-b border-[#E4E4E7] flex items-center justify-between">
        <div className="label-cap text-[10px]">Uji Coba Jawaban (tidak dikirim ke WA)</div>
        <button onClick={() => setMsgs([])} title="Ulang percakapan" className="p-1.5 hover:bg-[#F4F4F5]"><RotateCcw size={13} /></button>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2 bg-[#FAFAFA]">
        {msgs.length === 0 && <div className="text-center text-xs text-[#52525B] py-6">Ketik pesan seolah-olah Anda pelanggan, untuk melihat jawaban AI berdasarkan knowledge base yang sudah disimpan.</div>}
        {msgs.map((m, i) => (
          <div key={i} className={`flex ${m.from_me ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] px-3 py-2 text-sm whitespace-pre-wrap break-words ${m.from_me ? "bg-[#DCF8C6] border border-[#c6ecb0]" : "bg-white border border-[#E4E4E7]"}`}>
              {m.body}
              {m.handoff && <div className="mt-1 text-[10px] font-bold text-[#B45309]">⚑ Diserahkan ke admin{m.reason ? `: ${m.reason}` : ""} — admin akan dapat notifikasi WA</div>}
              {m.booking && <div className="mt-1 text-[10px] font-bold text-[#002FA7] whitespace-pre-wrap">🔔 Terdeteksi setuju dijadwalkan — admin akan dapat notifikasi WA:{"\n"}{m.summary}</div>}
            </div>
          </div>
        ))}
        {busy && <div className="text-xs text-[#52525B]">AI sedang mengetik…</div>}
        <div ref={bottomRef} />
      </div>
      <div className="border-t border-[#E4E4E7] p-3 flex gap-2">
        <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") send(); }} placeholder="Tulis pesan pelanggan…" className={inputCls} data-testid="ai-test-input" />
        <button onClick={send} disabled={busy || !text.trim()} className="px-4 bg-[#16A34A] text-white disabled:opacity-50" data-testid="ai-test-send"><Send size={14} /></button>
      </div>
    </div>
  );
}

export default function WaAiAgent() {
  // Password gate sama seperti halaman Pengaturan: diminta setiap kali halaman dibuka, tanpa caching.
  const [locked, setLocked] = useState(true);
  const [lockPwd, setLockPwd] = useState("");
  const [unlocking, setUnlocking] = useState(false);

  const submitUnlock = async (e) => {
    e.preventDefault();
    setUnlocking(true);
    try {
      await api.post("/settings/unlock", { password: lockPwd });
      setLocked(false);
      setLockPwd("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Password salah");
    } finally {
      setUnlocking(false);
    }
  };

  if (locked) {
    return (
      <Layout>
        <div className="max-w-md mx-auto mt-12 bg-white border border-[#E4E4E7]" data-testid="ai-lock-screen">
          <div className="px-6 py-4 border-b border-[#E4E4E7] flex items-center gap-2">
            <Lock size={14} />
            <div className="label-cap">Akses Terkunci</div>
          </div>
          <form onSubmit={submitUnlock} className="p-6 space-y-4">
            <div>
              <h2 className="font-heading text-2xl font-black mb-1">Password Pengaturan</h2>
              <p className="text-sm text-[#52525B]">Halaman AI Agent diproteksi. Masukkan password pengaturan untuk lanjut.</p>
            </div>
            <input
              type="password"
              autoFocus
              required
              value={lockPwd}
              onChange={(e) => setLockPwd(e.target.value)}
              placeholder="Password pengaturan"
              data-testid="ai-unlock-input"
              className="w-full px-3 py-2 border border-[#E4E4E7] text-sm outline-none focus:border-[#002FA7] bg-white font-mono"
            />
            <button type="submit" disabled={unlocking} data-testid="ai-unlock-button" className="w-full bg-[#002FA7] hover:bg-[#00227A] text-white text-xs font-bold uppercase tracking-wider px-6 py-3 flex items-center justify-center gap-2 disabled:opacity-60">
              <Lock size={14} /> {unlocking ? "Memverifikasi…" : "Buka AI Agent"}
            </button>
          </form>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="mb-6">
        <div className="label-cap mb-2">Pengaturan</div>
        <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl font-black tracking-tight flex items-center gap-2"><Bot size={30} className="text-[#16A34A]" /> AI Agent</h1>
        <p className="text-xs text-[#52525B] mt-2">Claude membalas otomatis pesan WhatsApp masuk 24 jam berdasarkan knowledge base. Admin bisa mengambil alih chat kapan saja dari halaman WhatsApp.</p>
      </div>
      <div className="space-y-6">
        <ConfigCard />
        <div className="grid lg:grid-cols-2 gap-6 items-start">
          <KnowledgeBase />
          <Tester />
        </div>
      </div>
    </Layout>
  );
}
